import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Box, Flex, useTheme, Badge, Textarea, Button} from '@chakra-ui/react';
import {TextG10, TextG12, TextG14} from '../typography';
import UserAvatar from '../UserAvatar';
import {User} from '../../hooks/useAuth';
import Icon from '../Icon';
import {calculateDistance} from '../../utils/distance';
import Modal from '../Modals/Modal';
import {reportUser} from '../../endpoints/api';
import {ToastMessage} from '../toast';
import useAuth from '../../hooks/useAuth';

const WALKABOUT_AMBASSADOR_ROLE_ID = 8;

interface UserListItem {
  user_id: number;
  name: string;
  surname: string;
  handle: string;
  profile_photo_url: string;
  rsvp_status?: string;
  status?: string;
  roles?: number[];
  latitude?: string | null;
  longitude?: string | null;
  created_at: string;
}

interface UserListCardProps {
  user: UserListItem;
  currentUserLat?: number;
  currentUserLong?: number;
  onMentionClick?: (userId: number, handle: string) => void;
  hostUserId?: number;
}

const UserListCard: React.FC<UserListCardProps> = ({
  user,
  currentUserLat,
  currentUserLong,
  onMentionClick,
  hostUserId,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {user: currentUser} = useAuth();
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const displayName = user.name || user.handle;
  const isGoing = user.rsvp_status === 'GOING';
  const isHost = hostUserId != null && user.user_id === hostUserId;
  const isCommunityConnector = user.roles?.includes(WALKABOUT_AMBASSADOR_ROLE_ID) || false;
  const isCurrentUser = currentUser?.id === user.user_id;
  const canReport = !isCurrentUser && !isCommunityConnector;

  // Calculate if user is close neighbor
  const isCloseNeighbor =
    currentUserLat && currentUserLong && user.latitude && user.longitude
      ? calculateDistance(
          {latitude: currentUserLat, longitude: currentUserLong},
          {latitude: parseFloat(user.latitude), longitude: parseFloat(user.longitude)},
          true // asFeet
        ) <= 2500
      : false;

  const mockUser: Partial<User> = {
    id: user.user_id,
    name: user.name,
    surname: user.surname,
    handle: user.handle,
    profilePhotoUrl: user.profile_photo_url,
  };

  const handleReportSubmit = async () => {
    setIsSubmitting(true);
    try {
      await reportUser(user.user_id, reportReason.trim() || null);
      ToastMessage({
        status: 'success',
        text: 'Report submitted successfully. Our team will review it shortly.',
      });
      setReportModalOpen(false);
      setReportReason('');
    } catch (error) {
      ToastMessage({
        status: 'error',
        text: 'Failed to submit report. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box
        position="relative"
        borderRadius="8px"
        bg="white"
        border={`1px solid ${theme.colors.lightestGrey}`}
        p={3}
        cursor="pointer"
        transition="all 0.2s"
        onClick={() => navigate(isCurrentUser ? '/my-profile' : `/neighbor/${user.user_id}`)}>
        <Flex align="center" gap={3}>
          <Box flexShrink={0} transition="opacity 0.2s">
            <UserAvatar
              user={mockUser as User}
              avatar_image={user.profile_photo_url}
              w="48px"
              h="48px"
            />
          </Box>

          <Flex direction="column" flex={1} overflow="hidden">
            <Flex align="left" gap={0} direction="column">
              <Flex align="center" gap={2}>
                <TextG14 fontWeight="600" noOfLines={1}>
                  {displayName}
                </TextG14>
              </Flex>
              {user.handle && (
                <TextG12 mt="0 !important" color={theme.colors.grey} noOfLines={1}>
                  @{user.handle}
                </TextG12>
              )}
            </Flex>
            <Flex gap="5px" mt={1}>
              {isHost && (
                <Badge
                  position="absolute"
                  top="0"
                  right="0"
                  borderRadius={'0px 6px 0px 8px'}
                  bg="rgba(196, 155, 49, 0.9)"
                  color="white"
                  fontSize="10px"
                  fontWeight="600"
                  px={2.5}
                  py={1}
                  textTransform="none"
                  letterSpacing="0.2px">
                  <TextG10>Host</TextG10>
                </Badge>
              )}
              {isCommunityConnector && !isHost && (
                <Badge
                  position="absolute"
                  top="0"
                  right="0"
                  borderRadius={'0px 6px 0px 8px'}
                  bg="rgba(224, 47, 132, 0.9)"
                  color="white"
                  fontSize="10px"
                  fontWeight="600"
                  px={2.5}
                  py={1}
                  textTransform="none"
                  letterSpacing="0.2px">
                  <TextG10>Community Connector</TextG10>
                </Badge>
              )}
              {isCurrentUser && !isCommunityConnector && !isHost && (
                <Badge
                  position="absolute"
                  top="0"
                  right="0"
                  borderRadius="0px 6px 0px 8px"
                  bg="rgba(168, 127, 255, 0.9)"
                  color="white"
                  fontSize="10px"
                  fontWeight="600"
                  px={2.5}
                  py={1}
                  textTransform="none"
                  letterSpacing="0.2px">
                  <TextG10>You</TextG10>
                </Badge>
              )}
              {isGoing && !isCurrentUser && !isCommunityConnector && !isHost && (
                <Badge
                  position="absolute"
                  top="0"
                  right="0"
                  borderRadius={isCloseNeighbor ? '0px 8px 0px 0px' : '0px 6px 0px 8px'}
                  bg="rgba(26, 135, 197, 0.9)"
                  color={'white'}
                  fontSize="10px"
                  fontWeight="600"
                  px={2.5}
                  py={1}
                  textTransform="none"
                  letterSpacing="0.2px">
                  <TextG10>Confirmed Going</TextG10>
                </Badge>
              )}
              {isCloseNeighbor && !isCommunityConnector && !isCurrentUser && !isHost && (
                <Badge
                  position="absolute"
                  top={isGoing ? '32px' : '0'}
                  right="0"
                  borderRadius={isGoing ? '0px 0px 0px 8px' : '0px 6px 0px 8px'}
                  bg="#D5F4E6"
                  color="#1B5E36"
                  fontSize="10px"
                  fontWeight="600"
                  px={2.5}
                  py={1}
                  textTransform="none"
                  letterSpacing="0.2px">
                  <TextG10>Close Neighbor</TextG10>
                </Badge>
              )}
            </Flex>
            <Flex flexDir="row" justifyContent="space-between" pb={1}>
              {!isCurrentUser && onMentionClick && (
                <Flex
                  style={{textDecoration: 'underline'}}
                  align="baseline"
                  color={theme.colors.blue}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMentionClick(user.user_id, user.handle);
                  }}>
                  <TextG12 color={theme.colors.blue} fontWeight="500">
                    @ Mention In Chat
                  </TextG12>
                </Flex>
              )}
              {canReport && (
                <Flex
                  style={{textDecoration: 'underline'}}
                  align="baseline"
                  color={theme.colors.grey}
                  onClick={(e) => {
                    e.stopPropagation();
                    setReportModalOpen(true);
                  }}>
                  <TextG12 color={theme.colors.grey} fontWeight="500">
                    Report User
                  </TextG12>
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Box>

      {/* Avatar Modal */}
      <Modal
        contentStyle={{width: 'fit-content'}}
        show={avatarModalOpen}
        parentCallback={() => setAvatarModalOpen(false)}
        isCentered
        closeButton>
        <Flex justify="center" align="center">
          <UserAvatar user={mockUser as User} avatar_image={user.profile_photo_url} size="max" />
        </Flex>
      </Modal>

      {/* Report User Modal */}
      <Modal
        show={reportModalOpen}
        parentCallback={() => {
          setReportModalOpen(false);
          setReportReason('');
        }}
        header={<TextG14 fontWeight="600">Report User</TextG14>}
        closeButton>
        <Flex direction="column" gap={3} p={4}>
          <TextG12 color={theme.colors.grey}>
            Please describe why you're reporting @{user.handle}. Our team will review this report.
          </TextG12>
          <Textarea
            placeholder="Reason for reporting..."
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            minH="120px"
            maxLength={1000}
          />
          <Flex gap={2} justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                setReportModalOpen(false);
                setReportReason('');
              }}
              disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              bg={theme.colors.blue}
              color="white"
              onClick={handleReportSubmit}
              isLoading={isSubmitting}
              _hover={{bg: theme.colors.darkBlue}}>
              Submit Report
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

export default UserListCard;
